import React, { useState } from 'react'
import { Check, X, GripVertical, RotateCcw, HelpCircle } from 'lucide-react'
import KaTeXRenderer from '../../../common/KaTeXRenderer'
import { LessonStatsService } from '../../../../services/stats/LessonStatsService'
import { soundService } from '../../../../services/audio/SoundEffectService'
import { celebrateCorrect, WRONG_ENCOURAGE } from '../effects/feedbackEffects'

interface SequenceQuestionProps {
  question: string
  items: string[]  // Items to be arranged (in random or given order)
  correctSequence: number[]  // Correct order as indices of items array
  explanation: string
  hint?: string
  difficulty?: 'easy' | 'medium' | 'hard'
  onComplete?: () => void
  lessonId?: string
}

/**
 * Shuffle array using Fisher-Yates algorithm
 */
const shuffleArray = (array: number[]): number[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

/**
 * Sequence Question - Component cho câu hỏi sắp xếp thứ tự (Drag & Drop)
 * Dùng chung cho tất cả các bài học
 */
const SequenceQuestion: React.FC<SequenceQuestionProps> = ({
  question,
  items,
  correctSequence,
  explanation,
  hint,
  difficulty = 'medium',
  onComplete,
  // Không default 'current-lesson' — để undefined cho LessonStatsService fallback currentLessonId
  lessonId
}) => {
  // ✅ Shuffle ngẫu nhiên khi khởi tạo
  const [currentOrder, setCurrentOrder] = useState<number[]>(() =>
    shuffleArray([...Array(items.length).keys()])
  )
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [showHint, setShowHint] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [hasRecordedStats, setHasRecordedStats] = useState(false)  // ✅ Đảm bảo chỉ ghi 1 lần

  const [nextInsertIndex, setNextInsertIndex] = useState(0)

  const handleDragStart = (index: number) => {
    if (isSubmitted) return
    setDraggedIndex(index)
    // If user drags, we assume they are taking manual control, so we mark all as "ordered" (visually)
    // to avoid confusion, or we could just leave it. 
    // Let's set it to max so the "Pool" visual effect goes away and it behaves like a normal list.
    setNextInsertIndex(items.length)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (isSubmitted) return
    setDragOverIndex(index)
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (isSubmitted || draggedIndex === null) return

    const newOrder = [...currentOrder]
    const draggedItem = newOrder[draggedIndex]

    // Remove from old position
    newOrder.splice(draggedIndex, 1)
    // Insert at new position
    newOrder.splice(dropIndex, 0, draggedItem)

    setCurrentOrder(newOrder)
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleItemClick = (index: number) => {
    if (isSubmitted) return

    const newOrder = [...currentOrder]
    const clickedItem = newOrder[index]

    // Remove item from current position
    newOrder.splice(index, 1)

    if (index < nextInsertIndex) {
      // Item was in "Ordered" zone -> Move to "Pool" (End of list)
      newOrder.push(clickedItem)
      setNextInsertIndex(prev => Math.max(0, prev - 1))
    } else {
      // Item was in "Pool" zone -> Move to "Ordered" (nextInsertIndex)
      newOrder.splice(nextInsertIndex, 0, clickedItem)
      setNextInsertIndex(prev => Math.min(items.length, prev + 1))
    }

    setCurrentOrder(newOrder)
  }

  const handleSubmit = () => {
    setIsSubmitted(true)
    setNextInsertIndex(items.length) // Show all ranks on submit

    const isCorrect = currentOrder.every((itemIndex, position) =>
      correctSequence[position] === itemIndex
    )

    // 🔊 Phát âm thanh feedback
    if (isCorrect) {
      soundService.playCorrect()
      celebrateCorrect()
    } else {
      soundService.playIncorrect()
    }

    // ✅ Ghi nhận vào stats CHỈ 1 LẦN
    if (!hasRecordedStats) {
      if (isCorrect) {
        LessonStatsService.recordCorrectAnswer(lessonId)
      } else {
        LessonStatsService.recordWrongAnswer(lessonId)
      }
      setHasRecordedStats(true)
    }

    if (isCorrect && onComplete) {
      setTimeout(() => onComplete(), 1500)
    }
  }

  const handleReset = () => {
    // ✅ Shuffle lại khi reset để tránh ghi nhớ
    setCurrentOrder(shuffleArray([...Array(items.length).keys()]))
    setIsSubmitted(false)
    setShowHint(false)
    setNextInsertIndex(0) // Reset to all pool
  }

  const isCorrect =
    isSubmitted &&
    currentOrder.every((itemIndex, position) => correctSequence[position] === itemIndex)

  const getItemStyle = (currentPosition: number) => {
    const itemIndex = currentOrder[currentPosition]

    if (!isSubmitted) {
      if (currentPosition === dragOverIndex) {
        return 'border-blue-400 bg-blue-100 scale-105'
      }
      if (currentPosition === draggedIndex) {
        return 'border-gray-400 bg-gray-100 opacity-50'
      }

      // Visual distinction for Ordered vs Pool
      if (currentPosition < nextInsertIndex) {
        return 'border-blue-300 bg-blue-50 hover:bg-blue-100' // Ordered
      } else {
        return 'border-gray-200 bg-white hover:bg-gray-50 border-dashed' // Pool
      }
    }

    // After submit
    const correctPosition = correctSequence.indexOf(itemIndex)
    if (correctPosition === currentPosition) {
      return 'border-green-500 bg-green-50'  // Đúng vị trí
    } else {
      return 'border-red-500 bg-red-50'  // Sai vị trí
    }
  }

  const getPositionIcon = (currentPosition: number) => {
    if (!isSubmitted) return null

    const itemIndex = currentOrder[currentPosition]
    const correctPosition = correctSequence.indexOf(itemIndex)

    if (correctPosition === currentPosition) {
      return <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
    } else {
      return <X className="w-5 h-5 text-red-600 flex-shrink-0" />
    }
  }

  const getDifficultyColor = () => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'hard': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-5 my-4 shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg font-bold text-gray-900">🔢</span>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getDifficultyColor()}`}>
              {difficulty === 'easy' ? 'Dễ' : difficulty === 'medium' ? 'Trung bình' : 'Khó'}
            </span>
            <span className="text-xs px-2 py-1 rounded-full font-medium bg-indigo-100 text-indigo-800">
              Sắp xếp thứ tự
            </span>
          </div>
          <div className="text-base font-semibold text-gray-800 leading-relaxed">
            <KaTeXRenderer content={question} />
          </div>
        </div>
      </div>

      {/* Instructions */}
      {!isSubmitted && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>📝 Hướng dẫn:</strong> Click vào các mục để chọn thứ tự (1, 2, 3...).
            Hoặc kéo thả để sắp xếp thủ công.
          </p>
        </div>
      )}

      {/* Draggable items */}
      <div className="space-y-3 mb-4">
        {currentOrder.map((itemIndex, currentPosition) => (
          <div
            key={`${itemIndex}-${currentPosition}`}
            draggable={!isSubmitted}
            onDragStart={() => handleDragStart(currentPosition)}
            onDragOver={(e) => handleDragOver(e, currentPosition)}
            onDrop={(e) => handleDrop(e, currentPosition)}
            onDragEnd={handleDragEnd}
            onClick={() => handleItemClick(currentPosition)}
            className={`
              flex items-center gap-3 p-4 rounded-lg border-2 transition-all cursor-pointer
              ${getItemStyle(currentPosition)}
              ${!isSubmitted && 'active:scale-[0.98]'}
            `}
          >
            {/* Drag handle */}
            {!isSubmitted && (
              <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0" />
            )}

            {/* Position number */}
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 transition-colors
              ${!isSubmitted
                ? (currentPosition < nextInsertIndex
                  ? 'bg-blue-500 text-white shadow-md' // Ordered
                  : 'bg-gray-100 text-gray-400')       // Pool
                : correctSequence.indexOf(itemIndex) === currentPosition
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }
            `}>
              {currentPosition < nextInsertIndex || isSubmitted ? currentPosition + 1 : '?'}
            </div>

            {/* Item text */}
            <div className="flex-1 text-sm font-medium text-gray-800">
              <KaTeXRenderer content={items[itemIndex]} />
            </div>

            {/* Result icon */}
            {getPositionIcon(currentPosition)}
          </div>
        ))}
      </div>

      {/* Hint button */}
      {hint && !isSubmitted && (
        <button
          onClick={() => setShowHint(!showHint)}
          className="mb-3 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          <HelpCircle className="w-4 h-4" />
          {showHint ? 'Ẩn gợi ý' : 'Hiện gợi ý'}
        </button>
      )}

      {/* Hint */}
      {showHint && !isSubmitted && (
        <div className="mb-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="text-sm text-yellow-800">
            <strong>💡 Gợi ý:</strong> <KaTeXRenderer content={hint || ''} />
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        {!isSubmitted ? (
          <>
            <button
              onClick={handleSubmit}
              className="px-6 py-2.5 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 active:scale-95 shadow-md transition-all"
            >
              Kiểm tra thứ tự
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2.5 rounded-lg font-medium text-gray-600 hover:text-gray-800 flex items-center gap-1"
            >
              <RotateCcw className="w-4 h-4" />
              Đặt lại
            </button>
          </>
        ) : (
          <button
            onClick={handleReset}
            className="px-6 py-2.5 rounded-lg font-semibold bg-gray-600 text-white hover:bg-gray-700 active:scale-95 shadow-md transition-all flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Làm lại
          </button>
        )}
      </div>

      {/* Result */}
      {isSubmitted && (
        <div
          className={`
            mt-4 p-4 rounded-lg border-2
            ${isCorrect
              ? 'bg-green-50 border-green-300'
              : 'bg-red-50 border-red-300'
            }
          `}
          style={{
            animation: 'slideUpFade 0.4s ease-out'
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="flex-shrink-0 mt-0.5"
              style={isCorrect ? { animation: 'gentleBounce 0.6s ease-in-out' } : {}}
            >
              {isCorrect ? (
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" strokeWidth={3} />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                  <X className="w-5 h-5 text-white" strokeWidth={3} />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h4 className={`font-bold text-base mb-2 ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                {isCorrect ? '🎉 Chính xác! Tuyệt vời!' : WRONG_ENCOURAGE}
              </h4>
              <div className="text-sm text-gray-800 leading-relaxed">
                <strong>Giải thích:</strong> <KaTeXRenderer content={explanation} />
              </div>

              {!isCorrect && (
                <div className="mt-3 p-3 bg-white rounded border border-gray-300">
                  <p className="text-sm font-bold text-gray-700 mb-2">Thứ tự đúng:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    {correctSequence.map((itemIndex, position) => (
                      <li key={position} className="text-sm text-gray-700">
                        <KaTeXRenderer content={items[itemIndex]} />
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SequenceQuestion

